import i18n from "i18n-js";

/**
 * Okta - underscores, locales
 * Okta emails - hyphens, languages
 * Digital - hyphens, languages
 * sfPlatform - underscores, languages
 * Trailhead - hyphens, languages
 * GlobalNav - hyphens, locales
 */
/**
 * @description the full list of digital supported Locales, in Okta's locale format, sorted with the primary locale first for each lang tag (ie. 'es_ES' for just 'es', or 'en_US' for 'en')
 */
const SUPPORTED_LOCALES = [
  "da_DK",
  "de_DE",
  "en_US",
  "en_AP",
  "en_AU",
  "en_CA",
  "en_EU",
  "en_GB",
  "en_IN",
  "eo_US",
  "es_ES",
  "es_MX",
  "fr_FR",
  "fi_FI",
  "fr_CA",
  "it_IT",
  "ja_JP",
  "ko_KR",
  "nl_NL",
  "no_NO",
  "pt_BR",
  "ru_RU",
  "sv_SE",
  "th_TH",
  "zh_CN",
  "zh_TW",
];
const DEFAULT_LOCALE = "en_US";
const DEFAULT_LANG = "en";

/**
 * @description The list of language tags supported by TBID
 */
const DIGITAL_SUPPORTED_LANGUAGES = [
  "da",
  "de",
  "en",
  "eo",
  "es-MX",
  "es",
  "fi",
  "fr",
  "it",
  "ja",
  "ko",
  "nl",
  "no",
  "pt-BR",
  "ru",
  "sv",
  "th",
  "zh-CN",
  "zh-TW",
];

/**
 * @description Profile services
 */
const SF_PLATFORM_SUPPORTED_LANGUAGES = [
  "en_US",
  "es",
  "de",
  "fr",
  "it",
  "ja",
  "ko",
  "nl_NL",
  "es_MX",
  "pt_BR",
  "zh_CN",
];
/**
 * @description List of Salesforce platform supported languages
 */
SF_PLATFORM_SUPPORTED_LANGUAGES.reduce((map, sfLocale) => {
  switch (sfLocale) {
    case "en_US":
      return {
        ...map,
        en: sfLocale,
      };
    case "nl_NL":
      return {
        ...map,
        nl: sfLocale,
      };
    case "es_MX":
    case "pt_BR":
    case "zh_CN": {
      const key = sfLocale.replace("_", "-");
      return {
        ...map,
        [key]: sfLocale,
      };
    }
    default:
      return {
        ...map,
        sfLocale,
      };
  }
}, {});

/**
 * @description List of Trailhead supported languages
 */
const TRAILHEAD_SUPPORTED_LANGUAGES = [
  "en",
  "es",
  "de",
  "fr",
  "it",
  "ja",
  "ko",
  "es-MX",
  "pt-BR",
  "zh-CN",
];
/**
 * @description List of languages supported in translating emails
 */
const OKTA_EMAIL_SUPPORTED_LANGUAGES = [
  "da",
  "de",
  "en",
  "es",
  "fi",
  "fr",
  "it",
  "ja",
  "ko",
  "nb",
  "nl-NL",
  "pt-BR",
  "ru",
  "sv",
  "th",
  "zh-CN",
  "zh-TW",
];

/**
 * Map Supported locales -> Okta values
 */
OKTA_EMAIL_SUPPORTED_LANGUAGES.reduce((map, oktaLocale) => {
  switch (oktaLocale) {
    case "nb":
      // Norwegian language code in Okta is 'nb' instead of 'no'
      return {
        ...map,
        no: oktaLocale,
      };
    case "nl-NL":
      return {
        ...map,
        nl: "nl-NL",
      };
    case "es":
      // Okta doesn't support Mexican Spanish explicitly though we support it
      return {
        ...map,
        oktaLocale,
        "es-MX": oktaLocale,
      };
    default:
      return {
        ...map,
        oktaLocale,
      };
  }
}, {});
function sourceLocaleFromBrowser() {
  if (typeof navigator === "undefined") {
    throw new Error("Unable to get locale, not running in browser");
  }
  let language = navigator.language;
  if (!language) {
    language = navigator.languages[0];
  }
  return language;
}
function getSupportedLocale({ langTag, regionTag }) {
  if (!regionTag) {
    // Unable to lookup locale without country, just use default for langTag
    // Find the first supported full locale for this language
    // NOTE: assumes langTag is supported by the list of locales
    return (
      SUPPORTED_LOCALES.find((supportedLocale) => {
        const [thisLocaleLang] = supportedLocale.split("_");
        return thisLocaleLang === langTag;
      }) ?? DEFAULT_LOCALE
    );
  }
  const desiredLocale = `${langTag}_${regionTag}`;
  let foundLocale = SUPPORTED_LOCALES.find(
    (supportedLocale) => supportedLocale.toLowerCase() === desiredLocale,
  );

  // Attempt to match on just the lang tag and get the default locale for that
  if (!foundLocale) {
    foundLocale = SUPPORTED_LOCALES.find((supportedLocale) => {
      const supportedLang = supportedLocale.split("_")[0];
      return supportedLang.toLowerCase() === langTag.toLowerCase();
    });
  }
  return foundLocale ?? DEFAULT_LOCALE;
}

/**
 * @param {string} [params.locale] - string to get locale from. Defaults to `navigator.language`
 * @param {string} [params.countryCode] - string to use as the country code for the locale
 * @param {LocaleFormat} [params.to=okta] - target format for the locale, defaults to 'okta'
 * @description Use to get a supported and properly formatted locale
 * @returns {SupportedLocale | GlobalNavSupportedLocale}
 */
function getLocale({ locale, countryCode, to = "okta" } = {}) {
  let rawLocale = locale;
  if (!rawLocale) {
    rawLocale = sourceLocaleFromBrowser();
  }

  // Parse out language and locale
  const regex = rawLocale.match(
    /^(?<lang>[a-z]{2})?(?:[_-])?(?<locale>[a-z]{2})?$/i,
  );

  // Create a valid tag
  // https://www.loc.gov/standards/iso639-2/php/English_list.php
  // https://datatracker.ietf.org/doc/html/rfc5646
  const tag =
    [regex?.groups?.lang, regex?.groups?.locale?.toUpperCase()]
      .filter(Boolean)
      .join("_") || rawLocale;

  // Normalize to Okta-style locale
  const normalizedLocale = tag.toLowerCase();
  const [langTag, normalizedCountry] = normalizedLocale.split("_");
  let regionTag = countryCode?.toLowerCase();
  if (!regionTag) {
    regionTag = normalizedCountry;
  }
  const supportedLocale = getSupportedLocale({
    langTag,
    regionTag,
  });
  if (to === "globalNav") {
    const globalNavLocale = langTag === "ru" ? DEFAULT_LOCALE : supportedLocale;
    return globalNavLocale.replace("_", "-");
  }
  return supportedLocale;
}
function getSupportedLang({ locale, supportedLangs }) {
  const mutableArr = [...supportedLangs]; // Simply to appease TS deities
  let foundLang = mutableArr.find(
    (lang) => lang.toLowerCase() === locale.toLowerCase(),
  );
  const langTag = locale.split(/[_|-]/)[0];
  if (!foundLang) {
    foundLang = mutableArr.find(
      (lang) => lang.toLowerCase() === langTag.toLowerCase(),
    );
  }

  // If still not found, fallback to the first locale that is supported for that langTag
  if (!foundLang) {
    foundLang = mutableArr.find((lang) => {
      const langLang = lang.split(/[_|-]/)[0];
      return langLang.toLowerCase() === langTag.toLowerCase();
    });
  }
  return foundLang;
}

/**
 * @param {string} [params.locale] - locale to get lang tag from, otherwise will try to use `navigator.language`
 * @param {LanguageFormat} [params.format=digital] - target to use the lang tag in, defaults to 'digital'
 * @description Use to get the properly formatted language
 * @returns {SupportedLanguage}
 */
function getLanguage({ locale, format = "digital" } = {}) {
  const supportedLocale = getLocale({
    locale,
  });
  const formattedLocale =
    format === "sfPlatform"
      ? supportedLocale.replace("-", "_")
      : supportedLocale.replace("_", "-");
  const supportedLangs =
    format === "trailhead"
      ? TRAILHEAD_SUPPORTED_LANGUAGES
      : format === "sfPlatform"
      ? SF_PLATFORM_SUPPORTED_LANGUAGES
      : DIGITAL_SUPPORTED_LANGUAGES;
  let supportedLang = getSupportedLang({
    locale: formattedLocale,
    supportedLangs,
  });
  if (!supportedLang) {
    supportedLang = format === "sfPlatform" ? DEFAULT_LOCALE : DEFAULT_LANG;
  }
  return format === "sfPlatform"
    ? supportedLang
    : supportedLang.replace("_", "-");
}

const DEFAULT_FALLBACKS = false;

/* i18n Configuration */
i18n.fallbacks = DEFAULT_FALLBACKS;

//@ts-ignore
i18n.missingTranslation = (key) => {
  const message = `missing "${key}" translation for locale "${i18n.locale}"`;
  return `[${message}]`;
};

//@ts-ignore
i18n.missingPlaceholder = (placeholder, label) => {
  return `[missing ${placeholder} value]`;
};
/* End i18n configuration */

let state = {
  configureHandlers: [],
  changeHandlers: [],
  i18n: [i18n],
  // This is arbitrarily high to avoid conflicting with old versions before the guids were unique
  previousId: 2000,
};
class I18n {
  id = this.getUniqueId();
  constructor(scope) {
    if (scope) this.addScope(scope);
  }
  t(scope, key, options) {
    return i18n.t(`${scope}_${this.id}.${key}`, options);
  }
  addScope(scope) {
    for (const [scopeName, locales] of Object.entries(scope)) {
      for (const [locale, labels] of Object.entries(locales)) {
        const localeKey = getLanguage({
          locale,
        });
        i18n.translations[localeKey] = {
          ...i18n.translations[localeKey],
          [`${scopeName}_${this.id}`]: labels,
        };
      }
    }
  }
  getUniqueId() {
    return (state.previousId += 1);
  }
}
function configure(configHandler) {
  state.configureHandlers.push(configHandler);
  for (const i18n of state.i18n) {
    configHandler(i18n);
  }
}
function registerLocaleChangeHandler(handler) {
  state.changeHandlers.push(handler);
  return () => {
    state.changeHandlers = state.changeHandlers.filter((h) => h !== handler);
  };
}
function initializeState(currentState, existingState) {
  if (!existingState) return currentState;

  // Most likely, i18n is the same between modules as the i18n-js module attaches to window and then exports any
  // previously set instance. To ensure backwards compatibility, and to guard against changes to the i18n-js module in
  // the future, we keep the array of instances here.
  if (!existingState.i18n.includes(i18n)) {
    existingState.i18n = [...existingState.i18n, i18n];
  }

  // Run configurations that happened before this module was loaded
  for (const configHandler of existingState.configureHandlers) {
    configHandler(i18n);
  }

  // Ensure the previousId doesn't match between runs
  existingState.previousId += 1;
  return existingState;
}

// This module may be executed multiple times from different sources
if (typeof window !== "undefined") {
  // @ts-ignore
  window["@sfdc-www/digital-i18n"] = state = initializeState(
    state,
    // @ts-ignore
    window["@sfdc-www/digital-i18n"],
  );
}

configure((i18n) => {
  // Use "en" labels as a fallback
  i18n.fallbacks = true;
});
const translators = new WeakMap();
class LabelsAdapter {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  i18n = undefined;
  config = null;
  get options() {
    if (!this.config) return {};
    return this.config.options || {};
  }
  get labels() {
    if (!this.config) return {};
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const keys = flattenKeys(this.config.locales.en);
    return {
      ...keys.reduce(
        (labels, key) =>
          Object.assign(labels, {
            [key]: this.i18n.t("default", key, this.options[key] || {}),
          }),
        {},
      ),
    };
  }
  removeLocaleChangeHandler = () => {};
  constructor(setValue) {
    this.setValue = setValue;
  }
  connect() {
    this.setValue(this.labels);
    this.removeLocaleChangeHandler = registerLocaleChangeHandler(() => {
      this.setValue(this.labels);
    });
  }
  disconnect() {
    this.removeLocaleChangeHandler();
  }
  update(config) {
    if (!this.config || config.locales !== this.config.locales) {
      this.i18n =
        translators.get(config.locales) ||
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        translators.set(config.locales, new I18n()).get(config.locales);
      this.i18n.addScope({
        default: config.locales,
      });
    }
    this.config = {
      ...config,
    };
    this.setValue(this.labels);
  }
}

/**
 * We should stop the "key flattening" when we reach to an object that has any of these reserved keys
 */
function reservedKeys() {
  // https://www.npmjs.com/package/i18n-js#pluralization
  const RESERVED_KEYWORDS_PLURALIZATION = ["zero", "one", "other"];
  // https://www.npmjs.com/package/i18n-js#i18nnumbertohuman
  const RESERVED_KEYWORDS_QUANTIFIERS_INTEGERS = [
    "unit",
    "ten",
    "hundred",
    "thousand",
    "million",
    "billion",
    "trillion",
    "quadrillion",
  ];
  const RESERVED_KEYWORDS_QUANTIFIERS_FRACTIONALS = [
    "deci",
    "centi",
    "mili",
    "micro",
    "nano",
    "pico",
    "femto",
  ];
  return [
    ...RESERVED_KEYWORDS_PLURALIZATION,
    ...RESERVED_KEYWORDS_QUANTIFIERS_INTEGERS,
    ...RESERVED_KEYWORDS_QUANTIFIERS_FRACTIONALS,
  ];
}
function flattenKeys(object, keyPath = "") {
  return Object.keys(object).flatMap((key) => {
    if (
      typeof object[key] === "object" &&
      !Object.keys(object[key]).some((key) => reservedKeys().includes(key))
    ) {
      return flattenKeys(object[key], `${keyPath}${key}.`);
    }
    return `${keyPath}${key}`;
  });
}

/**
 * Useful to convert a flattened object (like the one provided by the LabelsAdapter)
 * @returns the unflattened object that can be traversed using the dot ('.') notation in a html template
 * @see: https://www.geeksforgeeks.org/how-to-unflatten-an-object-with-the-paths-for-keys-in-javascript/
 */
//@ts-ignore
function unflattenObject(data) {
  const result = {};
  for (const i in data) {
    const keys = i.split(".");
    keys.reduce((acc, value, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return (
        //@ts-ignore
        acc[value] ||
        //@ts-ignore
        (acc[value] = isNaN(Number(keys[index + 1]))
          ? keys.length - 1 === index
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              data[i]
            : {}
          : [])
      );
    }, result);
  }
  return result;
}

export default LabelsAdapter;
export { flattenKeys, unflattenObject };
